import { api } from '../client/axiosClient';
import { CustomResponseEntity } from '../types/common';
import type {
  AnalyticsDashboardDto,
  AnalyticsDashboardCreateDto,
  AnalyticsDashboardUpdateDto,
  AnalyticsDashboardScopeFilterDto,
  AnalyticsGraphDto,
  AnalyticsGraphCreateDto,
  AnalyticsGraphUpdateDto,
  AnalyticsGraphDataResponseDto,
  DrillDownDataDto,
  FieldMetadataDto,
  ScopeFilterOptionDto,
  ProgramHierarchyDto,
  DataSourceCatalogDto,
} from '../types/dtos/analytics/AnalyticsDto';

/**
 * Analytics API Service
 * Handles all API calls for the Analytics Dashboard System
 *
 * URL Pattern: /api/v1/analytics
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId constructor parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * @deprecated organizationId constructor parameter is deprecated and no longer used.
 */
export class AnalyticsService {
  private static readonly BASE_PATH = '/api/v1/analytics';

  /**
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   */
  constructor(_organizationId?: string) {
    // organizationId no longer used - context comes from X-Organization-Id header
  }

  private get baseUrl(): string {
    return AnalyticsService.BASE_PATH;
  }

  // ==================== Dashboard CRUD ====================

  async findAllDashboards(): Promise<CustomResponseEntity<AnalyticsDashboardDto[]>> {
    return api.get(`${this.baseUrl}/dashboards`);
  }

  async findDashboardById(id: string): Promise<CustomResponseEntity<AnalyticsDashboardDto>> {
    return api.get(`${this.baseUrl}/dashboards/${id}`);
  }

  async createDashboard(dto: AnalyticsDashboardCreateDto): Promise<CustomResponseEntity<AnalyticsDashboardDto>> {
    return api.post(`${this.baseUrl}/dashboards`, dto);
  }

  async updateDashboard(
    id: string,
    dto: AnalyticsDashboardUpdateDto
  ): Promise<CustomResponseEntity<AnalyticsDashboardDto>> {
    return api.put(`${this.baseUrl}/dashboards/${id}`, dto);
  }

  async deleteDashboard(id: string): Promise<CustomResponseEntity<void>> {
    return api.delete(`${this.baseUrl}/dashboards/${id}`);
  }

  async duplicateDashboard(id: string): Promise<CustomResponseEntity<AnalyticsDashboardDto>> {
    return api.post(`${this.baseUrl}/dashboards/${id}/duplicate`);
  }

  // ==================== Scope Filters ====================

  async updateScopeFilters(
    dashboardId: string,
    scopeFilters: AnalyticsDashboardScopeFilterDto[]
  ): Promise<CustomResponseEntity<AnalyticsDashboardDto>> {
    return api.put(`${this.baseUrl}/dashboards/${dashboardId}/scope-filters`, scopeFilters);
  }

  // ==================== Graph Management ====================

  async addGraph(
    dashboardId: string,
    dto: AnalyticsGraphCreateDto
  ): Promise<CustomResponseEntity<AnalyticsGraphDto>> {
    return api.post(`${this.baseUrl}/dashboards/${dashboardId}/graphs`, dto);
  }

  async updateGraph(
    dashboardId: string,
    graphId: string,
    dto: AnalyticsGraphUpdateDto
  ): Promise<CustomResponseEntity<AnalyticsGraphDto>> {
    return api.put(`${this.baseUrl}/dashboards/${dashboardId}/graphs/${graphId}`, dto);
  }

  async deleteGraph(dashboardId: string, graphId: string): Promise<CustomResponseEntity<void>> {
    return api.delete(`${this.baseUrl}/dashboards/${dashboardId}/graphs/${graphId}`);
  }

  async duplicateGraph(
    dashboardId: string,
    graphId: string
  ): Promise<CustomResponseEntity<AnalyticsGraphDto>> {
    return api.post(`${this.baseUrl}/dashboards/${dashboardId}/graphs/${graphId}/duplicate`);
  }

  // ==================== Graph Data ====================

  async getDashboardGraphData(
    dashboardId: string
  ): Promise<CustomResponseEntity<AnalyticsGraphDataResponseDto[]>> {
    return api.get(`${this.baseUrl}/dashboards/${dashboardId}/data`);
  }

  async getDrillDownData(
    graphId: string,
    filterValue: string
  ): Promise<CustomResponseEntity<DrillDownDataDto>> {
    return api.get(`${this.baseUrl}/graphs/${graphId}/drilldown?filterValue=${encodeURIComponent(filterValue)}`);
  }

  // ==================== Metadata ====================

  async getAvailableFields(entity: string): Promise<CustomResponseEntity<FieldMetadataDto[]>> {
    return api.get(`${this.baseUrl}/fields?entity=${entity}`);
  }

  async getFieldsCatalog(): Promise<CustomResponseEntity<DataSourceCatalogDto[]>> {
    return api.get(`${this.baseUrl}/fields-catalog`);
  }

  async getScopeFilterOptions(filterType: string): Promise<CustomResponseEntity<ScopeFilterOptionDto[]>> {
    return api.get(`${this.baseUrl}/scope-filter-options?filterType=${filterType}`);
  }

  async getProgramHierarchy(): Promise<CustomResponseEntity<ProgramHierarchyDto[]>> {
    return api.get(`${this.baseUrl}/programs-hierarchy`);
  }
}

/**
 * Factory function to create an AnalyticsService instance
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 */
export const createAnalyticsService = (_organizationId?: string): AnalyticsService => {
  return new AnalyticsService(_organizationId);
};
